import React from "react";

/**
 * Beautiful 1200x628 share card for a single trade setup (entry/SL/target/qty).
 * Designed to look great in WhatsApp / Twitter previews.
 *
 * Props:
 *   symbol         e.g. "RELIANCE"     (optional)
 *   entry, stop, target, qty, capital, riskPct, rr
 *   isLong         boolean
 */
const fmt = (n) =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 })
    : "—";

export default function ShareTradeSetupCard({
  symbol,
  entry, stop, target, qty, capital, riskPct, rr,
  totalRisk, totalReward, riskPerShare, rewardPerShare,
  isLong = true,
}) {
  return (
    <div
      style={{
        width: 1200,
        height: 628,
        background: "linear-gradient(135deg, #0a0e0c 0%, #051210 60%, #02100c 100%)",
        color: "#e7f7ee",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        position: "relative",
        overflow: "hidden",
        padding: "48px 56px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      data-testid="share-trade-card"
    >
      {/* Background grid */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage:
          "linear-gradient(rgba(40,255,180,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(40,255,180,0.05) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        opacity: 0.5,
      }} />
      {/* Glow */}
      <div style={{
        position: "absolute",
        top: -150, right: -150, width: 480, height: 480, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(40,255,180,0.18), transparent 70%)",
        filter: "blur(40px)",
      }} />

      {/* Header */}
      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{
            fontSize: 14, letterSpacing: "0.3em", textTransform: "uppercase",
            color: "#28ffb4", fontWeight: 700, marginBottom: 16,
          }}>
            /// Trade Setup
          </div>
          {symbol && (
            <div style={{ fontSize: 56, fontWeight: 600, letterSpacing: "-0.02em", color: "#fff", marginBottom: 4 }}>
              {symbol}
            </div>
          )}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "6px 14px",
            border: `1px solid ${isLong ? "rgba(40,255,180,0.5)" : "rgba(255,90,90,0.5)"}`,
            background: isLong ? "rgba(40,255,180,0.1)" : "rgba(255,90,90,0.1)",
            color: isLong ? "#28ffb4" : "#ff6464",
            fontSize: 16, fontWeight: 600, letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}>
            {isLong ? "▲ LONG" : "▼ SHORT"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, letterSpacing: "0.2em", textTransform: "uppercase", color: "#7a8a82", marginBottom: 8 }}>
            R : R
          </div>
          <div style={{
            fontSize: 64, fontWeight: 700,
            color: rr >= 2 ? "#28ffb4" : rr >= 1.5 ? "#ffcc55" : "#ff6464",
            lineHeight: 1,
          }}>
            1 : {rr ? rr.toFixed(2) : "—"}
          </div>
          <div style={{ fontSize: 14, color: rr >= 2 ? "#28ffb4" : "#7a8a82", marginTop: 4 }}>
            {rr >= 2 ? "STRONG" : rr >= 1.5 ? "DECENT" : "WEAK"}
          </div>
        </div>
      </div>

      {/* Levels strip */}
      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>
        {[
          { label: "ENTRY",      value: `₹${fmt(entry)}`,  color: "#fff" },
          { label: "STOP LOSS",  value: `₹${fmt(stop)}`,   color: "#ff6464" },
          { label: "TARGET",     value: `₹${fmt(target)}`, color: "#28ffb4" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            padding: "20px 22px",
            border: "1px solid rgba(140,160,150,0.18)",
            background: "rgba(20,30,26,0.6)",
            borderRadius: 4,
          }}>
            <div style={{ fontSize: 12, letterSpacing: "0.25em", textTransform: "uppercase", color: "#7a8a82", marginBottom: 10 }}>
              {label}
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, color }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Position sizing */}
      <div style={{
        position: "relative",
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: 14, padding: "22px 26px",
        border: "1px solid rgba(40,255,180,0.3)",
        background: "rgba(40,255,180,0.06)", borderRadius: 4,
      }}>
        {[
          { label: "QTY", value: `${fmt(qty)} sh`, color: "#3eb8ff" },
          { label: "INVESTMENT", value: `₹${fmt(qty * entry)}`, color: "#fff" },
          { label: "RISK", value: `₹${fmt(totalRisk)}`, color: "#ff6464" },
          { label: "REWARD", value: `₹${fmt(totalReward)}`, color: "#28ffb4" },
        ].map(({ label, value, color }) => (
          <div key={label}>
            <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7a8a82", marginBottom: 8 }}>
              {label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 14, color: "#7a8a82" }}>
          {fmt(capital)} capital · {riskPct}% risk per trade
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          color: "#28ffb4", fontSize: 16, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
        }}>
          ▲ breakoutscanner.co.in
        </div>
      </div>
    </div>
  );
}
